/**
 * PMS workflow service — objective status transition logic
 * Requirements: 2.2, 2.3, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 5.1, 5.3, 5.4, 7.1, 7.2, 7.3, 7.4
 */

import {
  Objective,
  ObjectiveStatus,
  ObjectiveEvaluationSummary,
  ObjectiveClosureStatus,
  Review,
  ReviewWorkflowStatus,
  ReviewHistoryAction,
  ReviewHistoryEntry,
  ReviewDiscussionMessage,
  ObjectiveRating,
  ReviewCycle,
  ReviewCycleStatus,
  ReviewCycleType,
  ReviewPhase,
  ReviewType,
} from '../types/pms';
import type { CreateObjectiveInput, UpdateObjectiveInput } from '../types/pms';
import { validateCreateObjectiveInput, validateUpdateObjectiveInput } from '../types/pmsValidation';
import { sameIdentity } from './identity';

/**
 * Result of a workflow operation.
 */
export interface PMSWorkflowResult {
  success: boolean;
  objective?: Objective;
  errors: string[];
}

/**
 * Valid status transitions per the design state machine.
 *
 *   (new)         → Draft          (Employee saves)
 *   Draft         → Submitted      (Employee submits)
 *   Submitted     → Approved       (Manager approves)
 *   Submitted     → Returned       (Manager returns/rejects)
 *   Returned      → Draft          (Employee re-edits)
 *   Approved      → In_Progress    (Employee updates progress)
 *   Approved      → Submitted      (Employee edits structural fields)
 *   In_Progress   → Submitted      (Employee edits structural fields)
 *   In_Progress   → Under_Review   (Manager initiates review)
 *   Under_Review  → Completed      (Manager submits final review)
 */
export const VALID_TRANSITIONS: Record<ObjectiveStatus, ObjectiveStatus[]> = {
  [ObjectiveStatus.DRAFT]: [ObjectiveStatus.SUBMITTED],
  [ObjectiveStatus.SUBMITTED]: [ObjectiveStatus.APPROVED, ObjectiveStatus.RETURNED],
  [ObjectiveStatus.APPROVED]: [ObjectiveStatus.IN_PROGRESS, ObjectiveStatus.SUBMITTED],
  [ObjectiveStatus.RETURNED]: [ObjectiveStatus.DRAFT],
  [ObjectiveStatus.IN_PROGRESS]: [ObjectiveStatus.SUBMITTED, ObjectiveStatus.UNDER_REVIEW],
  [ObjectiveStatus.UNDER_REVIEW]: [ObjectiveStatus.COMPLETED],
  [ObjectiveStatus.COMPLETED]: [],
};

/**
 * Check whether a status transition is allowed.
 */
export function isValidTransition(
  from: ObjectiveStatus,
  to: ObjectiveStatus
): boolean {
  return (VALID_TRANSITIONS[from] ?? []).includes(to);
}

/**
 * Fields that send an approved objective back for re-approval when changed.
 *
 * This is every field that defines *what* the employee is measured on. Limiting it to title
 * and weightage let an employee rewrite the description, the measure of success, or the dates
 * of an already-approved objective without the manager ever being asked again.
 *
 * Progress percentage and comments are deliberately excluded: they change continuously
 * throughout the year, and routing each one through approval would bury the manager. A
 * progress update moves Approved → In_Progress instead.
 */
const STRUCTURAL_FIELDS: (keyof UpdateObjectiveInput)[] = [
  'title',
  'description',
  'measureOfSuccess',
  'startDate',
  'endDate',
  'weightage',
];

// ---- helpers ----

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function now(): string {
  return new Date().toISOString();
}

function cloneObjective(o: Objective, overrides: Partial<Objective>): Objective {
  return { ...o, ...overrides, updatedAt: now() };
}

/**
 * Check if the objective is locked from employee edits.
 * Submitted objectives are locked until manager acts.
 * HR Admin lock prevents all modifications.
 * Requirements: 7.1, 7.2
 */
export function isLockedForEmployee(objective: Objective): boolean {
  return objective.isLocked || objective.status === ObjectiveStatus.SUBMITTED;
}

/**
 * Check if the objective is locked by HR Admin (prevents all modifications).
 * Requirements: 15.1, 15.2
 */
export function isHRAdminLocked(objective: Objective): boolean {
  return objective.isLocked;
}

/** The one wording for an HR objective lock, so every surface says the same thing. */
export const OBJECTIVES_LOCKED_MESSAGE =
  '🔒 Objectives have been locked by HR. Contact HR for changes.';

/**
 * Whether HR's lock covers this objective — its own flag **or** its cycle's.
 *
 * HR locks at the cycle level: lockPMSObjectives writes isLocked/status onto the review cycle
 * row and deliberately does not fan out to every objective row. So `objective.isLocked` stays
 * false after an HR lock, and any surface reading it alone concluded the objective was
 * unlocked — which is why the per-row lock indicator never appeared in a locked cycle. The
 * controls were correctly disabled (the dashboard passes isReadOnly down), leaving the exact
 * outcome the spec calls out: silently disabled controls with nothing on the row saying why.
 *
 * Derived, not mirrored onto each row, matching deriveReviewStatus and getSelfReviewProgress:
 * one write to the cycle cannot leave N objective rows disagreeing with it, and unlock is
 * symmetric for free. Accepts either half as optional so callers that only hold one still work.
 *
 * `status === LOCKED` is checked as well as `isLocked` because the lock resolver sets both, and
 * a caller holding a cycle from a path that set only the status should not read as unlocked.
 */
export function isObjectiveLockedByHR(
  objective?: Objective | null,
  cycle?: ReviewCycle | null
): boolean {
  if (objective?.isLocked) return true;
  return !!cycle && (cycle.isLocked === true || cycle.status === ReviewCycleStatus.LOCKED);
}

// ---- public API ----

/**
 * Save a new objective as Draft.
 * Requirements: 2.2, 2.7
 */
export function saveDraft(
  input: CreateObjectiveInput,
  employeeId: string,
  employeeName: string,
  managerId: string,
  managerName: string
): PMSWorkflowResult {
  const validation = validateCreateObjectiveInput(input);
  if (!validation.isValid) {
    return { success: false, errors: validation.errors };
  }

  const ts = now();
  const objective: Objective = {
    id: generateId(),
    employeeId,
    employeeName,
    reviewCycleId: input.reviewCycleId,
    title: input.title,
    description: input.description,
    measureOfSuccess: input.measureOfSuccess,
    startDate: input.startDate,
    endDate: input.endDate,
    weightage: input.weightage,
    progressPercentage: 0,
    status: ObjectiveStatus.DRAFT,
    employeeComments: input.employeeComments || '',
    managerComments: '',
    managerId,
    managerName,
    isLocked: false,
    createdAt: ts,
    updatedAt: ts,
  };

  return { success: true, objective, errors: [] };
}

/**
 * Submit an objective for manager approval (Draft → Submitted).
 * Requirements: 2.3, 4.1
 */
export function submitObjective(
  objective: Objective
): PMSWorkflowResult {
  if (objective.isLocked) {
    return { success: false, errors: ['Objective is locked by HR Admin and cannot be modified'] };
  }

  if (!isValidTransition(objective.status, ObjectiveStatus.SUBMITTED)) {
    return {
      success: false,
      errors: [`Cannot submit an objective with status "${objective.status}". Only "Draft", "Approved", or "In_Progress" objectives can be submitted.`],
    };
  }

  return {
    success: true,
    objective: cloneObjective(objective, { status: ObjectiveStatus.SUBMITTED }),
    errors: [],
  };
}

/**
 * Approve a submitted objective (Submitted → Approved).
 * Requirements: 4.3
 */
export function approveObjective(
  objective: Objective
): PMSWorkflowResult {
  if (objective.isLocked) {
    return { success: false, errors: ['Objective is locked by HR Admin and cannot be modified'] };
  }

  if (objective.status !== ObjectiveStatus.SUBMITTED) {
    return {
      success: false,
      errors: [`Cannot approve an objective with status "${objective.status}". Only "Submitted" objectives can be approved.`],
    };
  }

  return {
    success: true,
    objective: cloneObjective(objective, { status: ObjectiveStatus.APPROVED }),
    errors: [],
  };
}

/**
 * Return a submitted objective to the employee (Submitted → Returned).
 * Manager comments are required.
 * Requirements: 4.4
 */
export function returnObjective(
  objective: Objective,
  managerComments?: string
): PMSWorkflowResult {
  if (objective.isLocked) {
    return { success: false, errors: ['Objective is locked by HR Admin and cannot be modified'] };
  }

  if (objective.status !== ObjectiveStatus.SUBMITTED) {
    return {
      success: false,
      errors: [`Cannot return an objective with status "${objective.status}". Only "Submitted" objectives can be returned.`],
    };
  }

  if (!managerComments || managerComments.trim().length === 0) {
    return { success: false, errors: ['Manager comments are required when returning an objective'] };
  }

  return {
    success: true,
    objective: cloneObjective(objective, {
      status: ObjectiveStatus.RETURNED,
      managerComments,
    }),
    errors: [],
  };
}

/**
 * Reject a submitted objective (Submitted → Returned with rejection reason).
 * Requirements: 4.6
 */
export function rejectObjective(
  objective: Objective,
  rejectionReason?: string
): PMSWorkflowResult {
  if (objective.isLocked) {
    return { success: false, errors: ['Objective is locked by HR Admin and cannot be modified'] };
  }

  if (objective.status !== ObjectiveStatus.SUBMITTED) {
    return {
      success: false,
      errors: [`Cannot reject an objective with status "${objective.status}". Only "Submitted" objectives can be rejected.`],
    };
  }

  if (!rejectionReason || rejectionReason.trim().length === 0) {
    return { success: false, errors: ['A rejection reason is required'] };
  }

  return {
    success: true,
    objective: cloneObjective(objective, {
      status: ObjectiveStatus.RETURNED,
      managerComments: rejectionReason,
    }),
    errors: [],
  };
}

/**
 * Update progress on an approved/in-progress objective.
 * If structural fields are changed (see STRUCTURAL_FIELDS), transitions back to Submitted for
 * re-approval. Progress and comment updates do not.
 * Requirements: 5.1, 5.3, 5.4, 7.1, 7.4
 */
export function updateProgress(
  objective: Objective,
  updates: UpdateObjectiveInput
): PMSWorkflowResult {
  if (objective.isLocked) {
    return { success: false, errors: ['Objective is locked by HR Admin and cannot be modified'] };
  }

  // Submitted objectives are locked from employee edits
  if (objective.status === ObjectiveStatus.SUBMITTED) {
    return { success: false, errors: ['Objective is pending approval and cannot be edited'] };
  }

  // Only Approved or In_Progress objectives can be updated by employees
  if (
    objective.status !== ObjectiveStatus.APPROVED &&
    objective.status !== ObjectiveStatus.IN_PROGRESS
  ) {
    return {
      success: false,
      errors: [`Cannot update an objective with status "${objective.status}". Only "Approved" or "In_Progress" objectives can be updated.`],
    };
  }

  const validation = validateUpdateObjectiveInput(updates);
  if (!validation.isValid) {
    return { success: false, errors: validation.errors };
  }

  // Check if structural fields are being edited → re-approval needed.
  // Compares against the current value rather than just testing for presence: edit forms
  // submit every field they render, so a pure comment or progress edit would otherwise echo
  // back an unchanged title and bounce the objective into re-approval for nothing.
  const hasStructuralChange = STRUCTURAL_FIELDS.some(
    (field) =>
      updates[field] !== undefined &&
      updates[field] !== (objective as unknown as Record<string, unknown>)[field]
  );

  const overrides: Partial<Objective> = {};

  if (updates.title !== undefined) overrides.title = updates.title;
  if (updates.description !== undefined) overrides.description = updates.description;
  if (updates.measureOfSuccess !== undefined) overrides.measureOfSuccess = updates.measureOfSuccess;
  if (updates.startDate !== undefined) overrides.startDate = updates.startDate;
  if (updates.endDate !== undefined) overrides.endDate = updates.endDate;
  if (updates.weightage !== undefined) overrides.weightage = updates.weightage;
  if (updates.progressPercentage !== undefined) overrides.progressPercentage = updates.progressPercentage;
  if (updates.employeeComments !== undefined) overrides.employeeComments = updates.employeeComments;

  if (hasStructuralChange) {
    // Structural edit → back to Submitted for re-approval
    overrides.status = ObjectiveStatus.SUBMITTED;
  } else if (objective.status === ObjectiveStatus.APPROVED && updates.progressPercentage !== undefined) {
    // Progress update on Approved → move to In_Progress
    overrides.status = ObjectiveStatus.IN_PROGRESS;
  }

  return {
    success: true,
    objective: cloneObjective(objective, overrides),
    errors: [],
  };
}

/**
 * Initiate a review for an in-progress objective (In_Progress → Under_Review).
 * Requirements: 8.1, 9.1
 */
export function initiateReview(
  objective: Objective
): PMSWorkflowResult {
  if (objective.isLocked) {
    // HR Admin lock still allows reviews per Req 15.2
    // But the transition itself should still work
  }

  if (objective.status !== ObjectiveStatus.IN_PROGRESS) {
    return {
      success: false,
      errors: [`Cannot initiate review for an objective with status "${objective.status}". Only "In_Progress" objectives can be reviewed.`],
    };
  }

  return {
    success: true,
    objective: cloneObjective(objective, { status: ObjectiveStatus.UNDER_REVIEW }),
    errors: [],
  };
}

/**
 * Complete a review (Under_Review → Completed).
 * Requirements: 9.4
 */
export function completeReview(
  objective: Objective
): PMSWorkflowResult {
  if (objective.status !== ObjectiveStatus.UNDER_REVIEW) {
    return {
      success: false,
      errors: [`Cannot complete review for an objective with status "${objective.status}". Only "Under_Review" objectives can be completed.`],
    };
  }

  return {
    success: true,
    objective: cloneObjective(objective, { status: ObjectiveStatus.COMPLETED }),
    errors: [],
  };
}

// ---- Review workflow helpers ----

export function deriveObjectiveClosureStatus(
  evaluationSummary?: ObjectiveEvaluationSummary | string
): ObjectiveClosureStatus {
  switch (evaluationSummary) {
    case ObjectiveEvaluationSummary.GOAL_ACHIEVED:
      return ObjectiveClosureStatus.COMPLETED;
    case ObjectiveEvaluationSummary.NOT_ACHIEVED:
      return ObjectiveClosureStatus.NOT_COMPLETED;
    default:
      return ObjectiveClosureStatus.PARTIALLY_COMPLETED;
  }
}

export function isObjectiveReviewed(
  objectiveId: string,
  objectiveRatings: Record<string, unknown>,
  objectiveComments: Record<string, string>,
  objectiveSummaries: Record<string, unknown>
): boolean {
  const hasRating = Boolean(objectiveRatings[objectiveId]);
  const hasComment = Boolean(objectiveComments[objectiveId]?.trim());
  const hasSummary = Boolean(objectiveSummaries[objectiveId]);
  return hasRating && hasComment && hasSummary;
}

export function countReviewedObjectives(
  objectives: Objective[],
  objectiveRatings: Record<string, unknown>,
  objectiveComments: Record<string, string>,
  objectiveSummaries: Record<string, unknown>
): { reviewed: number; total: number } {
  const total = objectives.length;
  const reviewed = objectives.filter((o) =>
    isObjectiveReviewed(o.id, objectiveRatings, objectiveComments, objectiveSummaries)
  ).length;
  return { reviewed, total };
}

export function validateAllObjectivesReviewed(
  objectives: Objective[],
  objectiveRatings: Record<string, unknown>,
  objectiveComments: Record<string, string>,
  objectiveSummaries: Record<string, unknown>
): { isValid: boolean; errors: string[]; pendingObjectives: Objective[] } {
  const pendingObjectives = objectives.filter(
    (o) => !isObjectiveReviewed(o.id, objectiveRatings, objectiveComments, objectiveSummaries)
  );
  const errors = pendingObjectives.map(
    (o, index) => `Objective ${index + 1} (${o.title}) has not been reviewed.`
  );
  return { isValid: pendingObjectives.length === 0, errors, pendingObjectives };
}

export function inferReviewWorkflowStatus(review: Review): ReviewWorkflowStatus {
  if (review.workflowStatus) return review.workflowStatus;
  if (review.isAcknowledged) return ReviewWorkflowStatus.COMPLETED;
  if (review.overallRating) return ReviewWorkflowStatus.SUBMITTED;
  return ReviewWorkflowStatus.DRAFT;
}

export function isReviewEditable(review?: Review | null, cycle?: ReviewCycle | null): boolean {
  if (!review) return true;
  if (cycle?.isLocked || cycle?.status === ReviewCycleStatus.LOCKED) return false;
  const status = inferReviewWorkflowStatus(review);
  return (
    status === ReviewWorkflowStatus.DRAFT ||
    status === ReviewWorkflowStatus.IN_PROGRESS ||
    status === ReviewWorkflowStatus.REOPEN_REQUESTED
  );
}

/**
 * Employee/manager self-review progress — deliberately derived from the two
 * submitted-at timestamps rather than stored as its own field, so it can never
 * drift out of sync with them. See the PMS review-cycle plan doc for the full
 * rationale (cycle-level reviewPhase vs. per-review progress are separate axes).
 */
export enum SelfReviewProgress {
  NOT_STARTED = 'NOT_STARTED',
  SELF_SUBMITTED = 'SELF_SUBMITTED',
  /** Manager sent it back; the employee owes a revision. */
  RETURNED = 'RETURNED',
  MANAGER_SUBMITTED = 'MANAGER_SUBMITTED',
}

/**
 * Whether the manager has sent this self-review back and the employee has not answered yet.
 *
 * Expressed as a comparison of two timestamps rather than a stored flag, which is what makes
 * repeated return/resubmit rounds work without any extra state: a return stamps returnedAt, a
 * resubmission stamps a newer employeeSubmittedAt, and whichever is later says whose turn it is.
 * A second return simply stamps a later returnedAt again.
 *
 * Deliberately not implemented by clearing employeeSubmittedAt — updateReview guards that field
 * with a truthy check (`if (input.employeeSubmittedAt)`) and so cannot write a null, the
 * DynamoDB resolver has the same shape, and destroying the original submission time would lose
 * audit history that the timeline reads.
 *
 * Lives in reviewMetadata, which is AWSJSON and already carries reopenedAt/reopenedBy — so this
 * needs no GraphQL schema or resolver change.
 */
export function isReturnedForRevision(review?: Review | null): boolean {
  const returnedAt = review?.reviewMetadata?.returnedAt as string | undefined;
  if (!returnedAt || review?.managerSubmittedAt) return false;
  const submittedAt = review?.employeeSubmittedAt;
  return !submittedAt || returnedAt > submittedAt;
}

/** The manager's stated reason for the outstanding return, if there is one. */
export function getReturnReason(review?: Review | null): string {
  if (!isReturnedForRevision(review)) return '';
  return String(review?.reviewMetadata?.returnReason || '');
}

export function getSelfReviewProgress(review?: Review | null): SelfReviewProgress {
  if (!review) return SelfReviewProgress.NOT_STARTED;
  if (review.managerSubmittedAt) return SelfReviewProgress.MANAGER_SUBMITTED;
  // Checked before employeeSubmittedAt: a returned review still carries the timestamp of the
  // submission that was returned, and must not read as still waiting on the manager.
  if (isReturnedForRevision(review)) return SelfReviewProgress.RETURNED;
  if (review.employeeSubmittedAt) return SelfReviewProgress.SELF_SUBMITTED;
  return SelfReviewProgress.NOT_STARTED;
}

/**
 * Employee can fill in or edit their self-review while the cycle is open and the ball is in
 * their court — either they have not submitted yet, or the manager has returned it.
 */
export function canEditSelfReview(cycle?: ReviewCycle | null, review?: Review | null): boolean {
  if (!cycle || cycle.reviewPhase !== ReviewPhase.OPEN) return false;
  const progress = getSelfReviewProgress(review);
  return progress === SelfReviewProgress.NOT_STARTED || progress === SelfReviewProgress.RETURNED;
}

/**
 * Manager may send the review back only while it is genuinely theirs to act on — the same
 * condition as evaluating it. Reusing canManagerReview rather than restating the rule keeps the
 * two actions from drifting apart: whatever gates Submit Review gates Return to Employee.
 */
export function canReturnToEmployee(cycle?: ReviewCycle | null, review?: Review | null): boolean {
  return canManagerReview(cycle, review);
}

/** Manager can rate only after the employee has submitted, while the cycle is still open, and before the manager has already submitted. */
export function canManagerReview(cycle?: ReviewCycle | null, review?: Review | null): boolean {
  if (!cycle || cycle.reviewPhase !== ReviewPhase.OPEN) return false;
  return getSelfReviewProgress(review) === SelfReviewProgress.SELF_SUBMITTED;
}

/**
 * Why the manager cannot evaluate yet — or null when they can.
 *
 * The wording lives with the rule rather than in a component because two separate surfaces
 * gate manager evaluation (Team Reviews' inline workspace and the ManagerReviewFormWrapper
 * route) and they were free to disagree: the route enforced canManagerReview while Team
 * Reviews gated only on isReviewEditable, which never looks at employeeSubmittedAt. A manager
 * shown a merely-disabled field also cannot tell whether to wait for the employee or for HR,
 * which is the whole question the tile answers.
 */
export function getManagerReviewBlockReason(
  cycle?: ReviewCycle | null,
  review?: Review | null,
  employeeName?: string
): string | null {
  if (canManagerReview(cycle, review)) return null;

  const who = employeeName?.trim() || 'the employee';

  if (cycle?.reviewPhase === ReviewPhase.CLOSED) {
    return 'HR has closed this review. Manager evaluation is read-only.';
  }

  switch (getSelfReviewProgress(review)) {
    case SelfReviewProgress.MANAGER_SUBMITTED:
      return 'You have already submitted this review. It is now read-only.';
    case SelfReviewProgress.SELF_SUBMITTED:
      // The employee has finished, so the only thing left holding it is the phase.
      return 'This review is not open. HR must open the review before manager evaluation.';
    default:
      return `Waiting for ${who} to submit their self-review. Manager evaluation unlocks once the employee submits.`;
  }
}

/**
 * Where a review sits in the enterprise workflow.
 *
 * Replaces the old Draft/Completed reading, which could not distinguish "nobody has done
 * anything" from "the employee has finished and the manager owes work" — the exact
 * distinction an approver needs. Derived from the cycle's reviewPhase plus the two
 * submitted-at timestamps, never stored, so it cannot drift from them.
 */
export enum ReviewStatus {
  WAITING_EMPLOYEE = 'Waiting Employee',
  /**
   * The same instant as WAITING_MANAGER, named from the employee's side.
   *
   * Both are listed in the workflow spec because the one state reads differently depending
   * on who is looking: the employee has finished ("submitted"), the manager has not started
   * ("waiting"). Which one you get is chosen by `audience` rather than by inspecting draft
   * manager data — the employee's own submission pre-populates objectiveRatings, so there is
   * no reliable signal for "the manager has started but not submitted".
   */
  SELF_REVIEW_SUBMITTED = 'Self Review Submitted',
  WAITING_MANAGER = 'Waiting Manager',
  MANAGER_COMPLETED = 'Manager Completed',
  CLOSED = 'Closed',
}

/**
 * Derive the workflow status of one review.
 *
 * CLOSED wins over everything: once HR closes the phase the record is read-only whatever
 * state it reached, and showing "Waiting Employee" on a closed review would invite work
 * that can no longer be saved.
 */
export function deriveReviewStatus(
  cycle?: ReviewCycle | null,
  review?: Review | null,
  audience: 'manager' | 'employee' = 'manager'
): ReviewStatus {
  if (cycle?.reviewPhase === ReviewPhase.CLOSED) return ReviewStatus.CLOSED;

  switch (getSelfReviewProgress(review)) {
    case SelfReviewProgress.MANAGER_SUBMITTED:
      return ReviewStatus.MANAGER_COMPLETED;
    case SelfReviewProgress.SELF_SUBMITTED:
      return audience === 'employee'
        ? ReviewStatus.SELF_REVIEW_SUBMITTED
        : ReviewStatus.WAITING_MANAGER;
    default:
      return ReviewStatus.WAITING_EMPLOYEE;
  }
}

/** The enum values are already display strings; this keeps call sites honest about intent. */
export function getReviewStatusLabel(status: ReviewStatus): string {
  return status;
}

/** Objective statuses that mean the employee has submitted it for approval at least once. */
const SUBMITTED_OR_BEYOND: ObjectiveStatus[] = [
  ObjectiveStatus.SUBMITTED,
  ObjectiveStatus.APPROVED,
  ObjectiveStatus.IN_PROGRESS,
  ObjectiveStatus.UNDER_REVIEW,
  ObjectiveStatus.COMPLETED,
];

export interface ReviewTimelineStep {
  key: string;
  label: string;
  done: boolean;
  /** The step the process is waiting on — at most one is ever true. */
  current: boolean;
  /** ISO timestamp, when the data model actually records one for this step. */
  timestamp?: string;
}

/**
 * The full objective→review journey, as ordered steps.
 *
 * Lives here rather than in the component so the ordering rules are testable without
 * rendering. Timestamps are only attached where the model genuinely stores one:
 * objective creation and the two review submissions. The phase transitions (submitted,
 * locked, opened, closed) are booleans in the current model with no dedicated
 * *-At column, so those steps show as reached without a time rather than borrowing
 * `updatedAt` — which any later unrelated edit would silently falsify.
 */
export function buildReviewTimeline(
  cycle?: ReviewCycle | null,
  review?: Review | null,
  objectives: Objective[] = []
): ReviewTimelineStep[] {
  const created = objectives
    .map(o => o.createdAt)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))[0];

  const anyObjectives = objectives.length > 0;
  const objectivesSubmitted =
    anyObjectives && objectives.every(o => SUBMITTED_OR_BEYOND.includes(o.status));
  // Either axis counts as locked: HR can lock the cycle, and the per-objective flag is what
  // the objective screens actually gate their controls on.
  const objectivesLocked =
    Boolean(cycle?.isLocked) || (anyObjectives && objectives.every(o => o.isLocked));
  const returnedAt = review?.reviewMetadata?.returnedAt as string | undefined;
  const reviewOpened =
    cycle?.reviewPhase === ReviewPhase.OPEN || cycle?.reviewPhase === ReviewPhase.CLOSED;
  const closed = cycle?.reviewPhase === ReviewPhase.CLOSED;

  const steps: Omit<ReviewTimelineStep, 'current'>[] = [
    { key: 'objective-created', label: 'Objective Created', done: anyObjectives, timestamp: created },
    { key: 'objectives-submitted', label: 'Objectives Submitted', done: objectivesSubmitted },
    { key: 'objectives-locked', label: 'Objectives Locked', done: objectivesLocked },
    { key: 'review-opened', label: 'Review Opened', done: reviewOpened },
    {
      key: 'employee-submitted',
      label: 'Employee Submitted',
      done: Boolean(review?.employeeSubmittedAt),
      timestamp: review?.employeeSubmittedAt,
    },
    /*
     * Only shown once a return has actually happened, so an ordinary review that never went
     * back keeps the seven-step shape it had before. `current` is derived from the first
     * unfinished step, so inserting a conditional step here cannot mislabel the ones after it.
     */
    ...(returnedAt
      ? [
          {
            key: 'manager-returned',
            label: 'Manager Returned',
            done: true,
            timestamp: returnedAt,
          },
          {
            key: 'employee-resubmitted',
            label: 'Employee Resubmitted',
            done: Boolean(review?.employeeSubmittedAt && review.employeeSubmittedAt > returnedAt),
            timestamp:
              review?.employeeSubmittedAt && review.employeeSubmittedAt > returnedAt
                ? review.employeeSubmittedAt
                : undefined,
          },
        ]
      : []),
    {
      key: 'manager-submitted',
      label: 'Manager Submitted',
      done: Boolean(review?.managerSubmittedAt),
      timestamp: review?.managerSubmittedAt,
    },
    { key: 'review-closed', label: 'Review Closed', done: closed },
  ];

  // "Current" is the first unfinished step, not "the one after the last finished one":
  // the sequence is enforced elsewhere, but a hand-edited or partially migrated record can
  // still show a gap, and this way the UI points at the earliest thing actually outstanding.
  const firstPending = steps.findIndex(s => !s.done);
  return steps.map((step, i) => ({ ...step, current: i === firstPending }));
}

export function canAcknowledgeReview(review: Review): boolean {
  const status = inferReviewWorkflowStatus(review);
  const isPublished =
    status === ReviewWorkflowStatus.SUBMITTED ||
    status === ReviewWorkflowStatus.COMPLETED;
  return isPublished && !review.isAcknowledged;
}

export function getReviewWorkflowStatusLabel(status?: ReviewWorkflowStatus): string {
  if (!status) return 'Pending';
  return String(status).replace(/_/g, ' ');
}

export function buildReviewHistoryEntry(
  action: ReviewHistoryAction | string,
  performedBy: string,
  performedByName: string,
  previousValue?: string,
  newValue?: string
): ReviewHistoryEntry {
  return {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    action,
    performedBy,
    performedByName,
    timestamp: new Date().toISOString(),
    previousValue,
    newValue,
  };
}

export function appendReviewHistory(
  review: Review,
  entry: ReviewHistoryEntry
): ReviewHistoryEntry[] {
  const existing = review.reviewHistory || review.reviewMetadata?.reviewHistory || [];
  return [...existing, entry];
}

/*
 * ─── Review discussion ─────────────────────────────────────────────────────────────────────
 *
 * A conversation between the employee and their manager, kept strictly apart from the manager's
 * evaluation fields. See ReviewMetadata.discussion for why it lives where it does.
 *
 * These three helpers are the only way the discussion should be read or written, so the
 * "separate from evaluation" rule is enforced in one place rather than at each call site.
 */

/** Messages oldest-first. Tolerates a record whose metadata predates the feature. */
export function getReviewDiscussion(review?: Review | null): ReviewDiscussionMessage[] {
  const messages = review?.reviewMetadata?.discussion;
  if (!Array.isArray(messages)) return [];
  return [...messages]
    .filter((m) => m && typeof m.text === 'string' && m.text.trim().length > 0)
    .sort((a, b) => String(a.timestamp).localeCompare(String(b.timestamp)));
}

export function buildReviewDiscussionMessage(
  authorId: string,
  authorName: string,
  authorRole: 'employee' | 'manager',
  text: string
): ReviewDiscussionMessage {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
    authorId,
    authorName,
    authorRole,
    text: text.trim(),
    timestamp: new Date().toISOString(),
  };
}

/**
 * The thread with one message appended — append-only, so neither side can edit or drop what the
 * other wrote. Returns the whole array because that is what updateReview must persist.
 */
export function appendReviewDiscussion(
  review: Review,
  message: ReviewDiscussionMessage
): ReviewDiscussionMessage[] {
  return [...getReviewDiscussion(review), message];
}

/**
 * The employee's self-assessed progress for an objective rating.
 *
 * Falls back to finalProgressPercentage only for records written before
 * employeeProgressPercentage existed — for those the two values were genuinely the same number,
 * so the fallback recovers the employee's figure rather than inventing one. Anything written
 * since carries the field explicitly, and the fallback never applies.
 *
 * Deliberately not `||`: a self-assessment of 0% is a real answer and must not fall through.
 */
export function employeeProgressOf(rating: ObjectiveRating): number {
  return rating.employeeProgressPercentage ?? rating.finalProgressPercentage ?? 0;
}

/**
 * The employee's own comment on one objective — what they wrote in their self review.
 *
 * ── Why this needed a function at all ───────────────────────────────────────
 *
 * The employee writes into `ObjectiveRating.comments` and the manager writes into
 * `managerFinalComment`, so on any record written since that split the employee’s note is
 * simply `comments` — the manager’s own submit preserves it verbatim rather than overwriting
 * it. Reading the field raw would still be wrong, because of what came before the split.
 *
 * ── The legacy record, and why it reports nothing ─────────────────────────────
 *
 * Before `managerFinalComment` existed, the manager’s text went into `comments` too and
 * overwrote whatever the employee had put there. Such a record carries a managerSubmittedAt
 * and NO managerFinalComment — and on it, `comments` holds the manager’s words. The
 * employee’s are gone and cannot be recovered from anywhere, so this reports nothing rather
 * than attributing the manager’s sentence to the employee.
 *
 * That test is the exact inverse of the legacy branch in managerCommentOf below, which is
 * what guarantees the two can never both claim the same string.
 *
 * Callers render their own placeholder, so this stays a data question.
 */
export function employeeCommentOf(
  rating: Pick<ObjectiveRating, 'managerFinalComment' | 'comments'>,
  review?: Pick<Review, 'managerSubmittedAt'>
): string {
  const written = (rating.comments || '').trim();
  if (!written) return '';

  /* A legacy record: the manager submitted, and their text is what is in `comments`. */
  if (review?.managerSubmittedAt && !(rating.managerFinalComment || '').trim()) return '';

  return written;
}

/**
 * The manager's comment on one objective — empty until the manager has actually evaluated.
 *
 * `ObjectiveRating.comments` is written by BOTH sides: the self-review form puts the employee's
 * per-objective note there, and the manager's review form writes its text to `comments` and
 * `managerFinalComment` together. So `managerFinalComment || comments` — which is what every
 * "Manager Comment" column used to render — printed the *employee's own* words under the
 * manager's heading the moment a self-review was submitted, before any manager had opened it.
 *
 * The fallback is kept, but gated on the manager having submitted. That is what makes it a
 * backward-compatibility path rather than a leak: a record written by a manager before
 * `managerFinalComment` existed carries the manager's text in `comments` and a
 * `managerSubmittedAt`, so it still reads correctly; a freshly submitted self-review has no
 * `managerSubmittedAt` and correctly reads as empty.
 *
 * Callers render their own placeholder ('Not provided') so this stays a data question.
 */
export function managerCommentOf(
  rating: Pick<ObjectiveRating, 'managerFinalComment' | 'comments'>,
  review?: Pick<Review, 'managerSubmittedAt'>
): string {
  /*
   * Gated on managerSubmittedAt in BOTH branches, not just the legacy one.
   *
   * The earlier version returned `managerFinalComment` unconditionally and only gated the
   * `comments` fallback — which was not enough, because the field itself had already been
   * contaminated. The manager's comment box used to be pre-filled from the employee's note, so
   * any Save Draft persisted that note into `managerFinalComment` on the review record. From then
   * on the value was indistinguishable from a real manager comment by inspection, and this
   * function happily returned it on a review with no manager submission at all.
   *
   * A manager comment is a statement the manager has made. Until they submit, they have not made
   * it, whatever a half-saved draft left in the column — so nothing is reported here. See
   * managerCommentDraftOf for the editable input, which has a different job and must keep a
   * manager's genuine work in progress.
   */
  if (!review?.managerSubmittedAt) return '';
  const explicit = (rating.managerFinalComment || '').trim();
  if (explicit) return explicit;
  // Legacy records only — see above.
  return (rating.comments || '').trim();
}

/**
 * What to seed the manager's editable comment box with.
 *
 * Deliberately a different question from managerCommentOf, which reports what the manager has
 * *stated*. This one has to keep a manager's genuine unfinished draft — losing it every time they
 * reopen the form would be its own bug — while never handing them the employee's words to save
 * back as their own verdict.
 *
 * Two rules do that:
 *
 *  1. Only ever `managerFinalComment` from THIS review's rating. Never `comments`, which belongs
 *     to the employee, and never the Objective record's managerFinalComment — see the note in
 *     TeamReviews' prefill for why that one crosses review cycles.
 *
 *  2. A stored value equal to the employee's own note is treated as contamination, not as the
 *     manager's text. That is exactly what the old prefill wrote on every draft save, and it is
 *     the only way to tell the two apart after the fact: the field carries no authorship. A
 *     manager who genuinely typed the employee's words verbatim retypes them — a trade worth
 *     making against handing them somebody else's comment to sign.
 *
 * Once the manager has submitted, their comment is authoritative and returned as-is.
 */
export function managerCommentDraftOf(
  rating: Pick<ObjectiveRating, 'managerFinalComment' | 'comments'>,
  review?: Pick<Review, 'managerSubmittedAt'>
): string {
  const stored = (rating.managerFinalComment || '').trim();
  if (!stored) return '';
  if (review?.managerSubmittedAt) return stored;
  const employeeNote = (rating.comments || '').trim();
  return stored === employeeNote ? '' : stored;
}

/**
 * Whether the MANAGER has evaluated this objective — from manager-owned data only.
 *
 * The reviewed count and the row checkmarks used to be computed from the form's current state:
 * "has a comment", "has a final progress number", "has an evaluation". Every one of those was
 * pre-filled from something the manager had not written — the employee's note, the employee's
 * self-assessed percentage, and the shared Objective record — so a review the manager had never
 * opened reported "Reviewed Objectives 2/2" with green ticks beside both rows, next to a
 * Manager Completed count of 0. Two parts of the same screen disagreeing about the same fact.
 *
 * evaluationSummary is the honest marker: the manager's form is the only thing that writes one,
 * and the self review never does. managerFinalComment counts too, for a record whose evaluation
 * predates that field.
 */
export function isObjectiveManagerEvaluated(
  rating?: Pick<ObjectiveRating, 'managerFinalComment' | 'comments' | 'evaluationSummary'>,
  review?: Pick<Review, 'managerSubmittedAt'>
): boolean {
  if (!rating) return false;
  if (rating.evaluationSummary) return true;
  return !!managerCommentDraftOf(rating, review);
}

/**
 * Placeholder reviewer identities that must never be stamped onto a review as if they were a
 * person.
 *
 * `createObjective` stores `managerId || 'unassigned'` and `managerName || 'Unassigned'`, so an
 * objective saved before its manager could be resolved carries those literal strings rather than
 * an empty field. Both are truthy, so `objectives.find(o => o.managerId)` happily selected such an
 * objective and the review was stamped `reviewerName: 'Unassigned'` — which then rendered as the
 * word "Unassigned" beside the Manager label, sailing past the `|| 'Not assigned'` guard there
 * because a sentinel is not a falsy value.
 *
 * Matched case-insensitively and after trimming, because the pair is written with different
 * casing ('unassigned' for the id, 'Unassigned' for the name).
 */
const UNRESOLVED_REVIEWER_VALUES = new Set(['', 'unassigned', 'not assigned', 'none', 'n/a', 'null', 'undefined']);

/** Whether a stored reviewer id or name names a real person rather than a placeholder. */
export function isResolvedReviewerValue(value?: string | null): boolean {
  return !UNRESOLVED_REVIEWER_VALUES.has(String(value ?? '').trim().toLowerCase());
}

/** One possible source of the reviewer, in the caller's order of preference. */
export interface ReviewerCandidate {
  id?: string | null;
  name?: string | null;
}

/**
 * The reviewer to stamp on a review, from candidate sources in priority order.
 *
 * Id and name are resolved independently on purpose: the authoritative *reporting line* and the
 * authoritative *display name* do not always live in the same record. An objective typically
 * carries the correct manager email alongside the 'Unassigned' name placeholder, while the
 * directory carries the real name — so taking both halves from the first candidate would keep the
 * placeholder, and taking them from different people would be worse still.
 *
 * The name is therefore only accepted from a candidate that agrees with the chosen id, compared
 * with sameIdentity so a manager's two domain addresses count as one person.
 *
 * Returns undefined for either half when nothing resolves. Callers omit the field rather than
 * writing a blank, so an unresolved reviewer stays visibly absent instead of looking deliberately
 * cleared.
 */
export function resolveReviewerIdentity(
  candidates: ReviewerCandidate[]
): { reviewerId?: string; reviewerName?: string } {
  const usable = candidates.filter(Boolean);
  const withId = usable.find(c => isResolvedReviewerValue(c.id));
  const reviewerId = withId ? String(withId.id).trim() : undefined;
  if (!reviewerId) return {};

  const named = usable.find(
    c => isResolvedReviewerValue(c.name) && sameIdentity(String(c.id ?? ''), reviewerId)
  );
  return { reviewerId, reviewerName: named ? String(named.name).trim() : undefined };
}

/**
 * The review record that already exists for one employee in one review cycle, if any.
 *
 * Keyed on employeeId **and reviewCycleId** together, never on the employee plus the annual
 * objective cycle. Several review cycles live under one annual cycle — Review 1, Review 2,
 * Review 3 — and they are separate reviews with separate lifecycles, so a lookup that stops at
 * the annual cycle finds Review 2 when asked about Review 3 and reports it as already existing.
 *
 * reviewType is compared as well because one cycle can carry a mid-year and an end-year record,
 * and those are genuinely different reviews of the same employee in the same cycle.
 *
 * sameIdentity, not `===`: a review's employeeId is whichever of the person's two domain
 * addresses was current when it was raised, so an exact match would miss half of them and report
 * "no existing review" for someone who has one.
 */
export function findReviewForCycle(
  reviews: Review[],
  employeeId: string,
  reviewCycleId: string,
  reviewType?: string
): Review | undefined {
  if (!employeeId || !reviewCycleId) return undefined;
  return reviews.find(review =>
    sameIdentity(review.employeeId, employeeId)
    && review.reviewCycleId === reviewCycleId
    && (!reviewType || review.reviewType === reviewType)
  );
}

/*
 * ─── One review per employee per review cycle ───────────────────────────────────────────────
 *
 * THE DUPLICATE-REVIEW BUG.
 *
 * Two code paths created reviews and each derived reviewType from the cycle its own way:
 *
 *   manager handleCreate:   HIGH_LEVEL → QUARTERLY,  else → END_YEAR
 *   employee submitSelfReview: YEAR_END → END_YEAR,  else → MID_YEAR
 *
 * They agree only when reviewCycleType is exactly YEAR_END. For a HIGH_LEVEL cycle, and for the
 * common case of a cycle with no reviewCycleType at all, they disagree — and submitSelfReview
 * looked for its existing record with `.find(r => r.reviewType === input.reviewType)`. That
 * lookup missed the manager's record, fell through to the create branch, and wrote a SECOND
 * review for the same employee and the same reviewCycleId.
 *
 * The two records then derive opposite statuses, because status is derived from the submission
 * timestamps each record carries:
 *
 *   manager-created record  → no employeeSubmittedAt → WAITING_EMPLOYEE
 *   employee-created record → employeeSubmittedAt    → WAITING_MANAGER
 *
 * which is both reported symptoms at once: the manager's queue showing one employee twice with
 * contradictory statuses, and "Start Self Review" still offered after submitting — because
 * `reviews.find(r => r.reviewCycleId === id)` returned whichever record happened to come first,
 * and if that was the manager's it had no employeeSubmittedAt.
 *
 * resolveReviewTypeForCycle removes the divergence at the source. resolveCanonicalReview makes
 * every reader agree on one record, which is what corrects the display for duplicates ALREADY
 * in the data — nothing is deleted here.
 */

/**
 * The review type for a cycle — the single derivation both creation paths must use.
 *
 * END_YEAR is the default for a cycle with no reviewCycleType because that is what the manager
 * path has always written, so it is what the existing records carry.
 */
export function resolveReviewTypeForCycle(cycle?: ReviewCycle | null): ReviewType {
  if (cycle?.reviewCycleType === ReviewCycleType.HIGH_LEVEL) return ReviewType.QUARTERLY;
  return ReviewType.END_YEAR;
}

/** How far through the workflow a record has got. Higher wins when duplicates disagree. */
function reviewProgressRank(review: Review): number {
  if (review.managerSubmittedAt) return 3;
  if (review.employeeSubmittedAt) return 2;
  // Some content but no submission — a draft somebody has touched.
  if (review.overallRating || (review.objectiveRatings?.length ?? 0) > 0) return 1;
  return 0;
}

/**
 * Every review for one employee in one review cycle, most-progressed first.
 *
 * Deliberately keyed on employee + reviewCycleId and NOT on reviewType: reviewType is exactly
 * the field the two creation paths disagreed about, so keying on it is what let the duplicate
 * hide. sameIdentity because a review may be filed under the employee's other domain address.
 */
export function findReviewsForEmployeeCycle(
  reviews: Review[],
  employeeId: string,
  reviewCycleId: string
): Review[] {
  if (!employeeId || !reviewCycleId) return [];
  return reviews
    .filter(review =>
      sameIdentity(review.employeeId, employeeId) && review.reviewCycleId === reviewCycleId
    )
    .sort((a, b) => {
      const byRank = reviewProgressRank(b) - reviewProgressRank(a);
      if (byRank !== 0) return byRank;
      // Same rank: the most recently touched record is the one people have been working in.
      return String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || ''));
    });
}

/**
 * THE review for an employee in a cycle. When duplicates exist, the furthest-progressed record
 * wins — so a submitted self-review is never masked by a stale untouched twin, whichever order
 * the API happened to return them in.
 */
export function resolveCanonicalReview(
  reviews: Review[],
  employeeId: string,
  reviewCycleId: string
): Review | undefined {
  return findReviewsForEmployeeCycle(reviews, employeeId, reviewCycleId)[0];
}

/**
 * Collapses duplicate records so each employee+cycle pair appears once, canonical record kept.
 * Order of first appearance is preserved so an existing sort is not disturbed.
 */
export function dedupeReviewsByEmployeeCycle(reviews: Review[]): Review[] {
  const keptIds = new Set<string>();
  const result: Review[] = [];
  for (const review of reviews) {
    const canonical = resolveCanonicalReview(reviews, review.employeeId, review.reviewCycleId);
    const id = canonical?.id;
    if (!id || keptIds.has(id)) continue;
    keptIds.add(id);
    result.push(canonical);
  }
  return result;
}

/**
 * Whether a review cycle can be the target of a NEW review.
 *
 * A cycle HR has closed cannot: closeReviewCycle writes `reviewPhase: CLOSED` and deliberately
 * leaves `status` alone, so a closed review stays ACTIVE and therefore kept passing
 * filterActiveCycles — which is how a closed Review 2 remained selectable in Create Review and a
 * "new" review created against it inherited the closed phase, the closed timeline and every other
 * cycle-derived piece of state.
 *
 * Phase and status are both checked because they answer different questions and either one can
 * rule the cycle out: a Locked or Completed cycle is not accepting new reviews either.
 */
export function canAcceptNewReview(cycle: ReviewCycle): boolean {
  if (!cycle.annualCycleId) return false; // annual parents are containers, not reviews
  if (cycle.reviewPhase === ReviewPhase.CLOSED) return false;
  if (cycle.isLocked) return false;
  return cycle.status === ReviewCycleStatus.ACTIVE;
}

/*
 * ─── The review cycle lifecycle ───────────────────────────────────────────────
 *
 * A cycle is described by TWO independent fields, and that is the whole source of the
 * trouble:
 *
 *   status       DRAFT → ACTIVE → COMPLETED. Whether employees can see the cycle at all.
 *   reviewPhase  NOT_STARTED → OPEN → CLOSED. Whether self-reviews are being accepted.
 *
 * Nothing tied them together, so Review Cycle Configuration offered "Publish to Employees"
 * and "Open Review" side by side on a DRAFT cycle — the second being gated only on
 * `status !== COMPLETED && reviewPhase !== OPEN`, which a brand new cycle satisfies. HR was
 * shown two first steps and no indication which came first, and opening reviews on an
 * unpublished cycle produces a cycle accepting self-reviews that no employee can see.
 *
 * They are ordered here, in one place both the buttons and any later caller can ask.
 */

/** A cycle can be published to employees only while it is still a draft. */
export function canPublishCycle(cycle: Pick<ReviewCycle, 'status'>): boolean {
  return cycle.status === ReviewCycleStatus.DRAFT;
}

/**
 * Reviews can be opened only on a cycle employees can already see.
 *
 * This is the gate that was missing. Publishing is what makes the cycle visible, so opening
 * reviews before it is a state with no meaning: the cycle would be accepting self-reviews
 * from people who cannot reach it.
 *
 * A CLOSED phase can be reopened, deliberately — a late submission is a real reason for HR to
 * open a review again, and refusing it would make Close Review an irreversible mistake.
 */
export function canOpenReview(cycle: Pick<ReviewCycle, 'status' | 'reviewPhase'>): boolean {
  return cycle.status === ReviewCycleStatus.ACTIVE && cycle.reviewPhase !== ReviewPhase.OPEN;
}

/** Reviews can be closed only while they are open. */
export function canCloseReview(cycle: Pick<ReviewCycle, 'reviewPhase'>): boolean {
  return cycle.reviewPhase === ReviewPhase.OPEN;
}

/**
 * A cycle can be completed once it is published and its reviews are not still open.
 *
 * The open-phase condition is the same ordering point as above, read from the other end:
 * completing a cycle while employees are mid-self-review strands every review in flight.
 * Close Review first — which is the button offered in its place.
 */
export function canCompleteCycle(
  cycle: Pick<ReviewCycle, 'status' | 'reviewPhase'>
): boolean {
  return cycle.status === ReviewCycleStatus.ACTIVE && cycle.reviewPhase !== ReviewPhase.OPEN;
}

export function enrichObjectiveRatings(
  ratings: ObjectiveRating[]
): ObjectiveRating[] {
  return ratings.map((r) => ({
    ...r,
    /*
     * Derived only from an evaluation the manager actually made.
     *
     * deriveObjectiveClosureStatus answers `PARTIALLY_COMPLETED` for anything it does not
     * recognise, undefined included — sensible for a real value it cannot map, but applied to an
     * *absent* evaluation it invents a closure status for an objective nobody has evaluated. That
     * is the same fabrication as the `|| PARTIALLY_ACHIEVED` default in buildReviewRatings, one
     * field over, and it would have kept "Partially Completed" on screen via ReviewDetail's
     * `closureStatus || evaluationSummary` even after that default was removed.
     *
     * deriveObjectiveClosureStatus itself is unchanged: its default is correct for the case it
     * was written for, and this is the only caller.
     */
    closureStatus: r.closureStatus
      || (r.evaluationSummary ? deriveObjectiveClosureStatus(r.evaluationSummary) : undefined),
    isReviewed: Boolean(r.rating && (r.managerFinalComment || r.comments) && r.evaluationSummary),
  }));
}

/**
 * The Team Reviews dashboard headline counts, in the terms the workflow actually has.
 *
 * Counted per *team member*, not per review record, which is the point. The previous
 * Pending/Completed pair came from inferReviewWorkflowStatus — which reads workflowStatus,
 * overallRating and isAcknowledged and never looks at the two submitted-at timestamps. Two
 * consequences, both of which mislead the manager reading the tiles:
 *
 *  1. A finished self-review still awaiting the manager counted as "Pending", indistinguishable
 *     from one the employee had not started. That is precisely the "who owes work" question the
 *     dashboard exists to answer.
 *  2. A member with no review record at all was counted nowhere, so the tiles silently summed to
 *     fewer than the team. Starting from the member list instead means everyone lands in exactly
 *     one bucket, and waitingEmployee + selfReviewSubmitted + managerCompleted === employees.
 *
 * Matched with sameIdentity because a review's employeeId is whichever of the member's two
 * domain addresses was current when it was raised.
 */
export interface ManagerReviewScope {
  /** The cycle chosen in the dropdown. A review from any other cycle is never in scope. */
  selectedCycleId: string;
  /** Team roster, lower-cased. Empty when the user directory could not be loaded. */
  teamMemberEmails: Set<string>;
  /** The signed-in manager, matched against the reviewer recorded on the review. */
  managerId: string;
  isSuperAdmin?: boolean;
}

/**
 * Whether one review belongs on this manager's Team Reviews screen.
 *
 * Extracted from the component so the two rules that matter can be tested without rendering:
 * a review from another cycle is never shown (which is what keeps selecting a cycle from a
 * different annual objective cycle from bleeding records across), and a review is in scope
 * either by team membership *or* by naming this manager as its reviewer.
 *
 * The second route is the fix for the screen going blank: membership was the only way in, and
 * the roster comes from a Cognito directory read with its own failure mode that surfaces as an
 * empty array. When that read failed, reviews that had loaded correctly were filtered away and
 * the cycle was reported as having no records at all.
 */
export function isReviewInManagerScope(review: Review, scope: ManagerReviewScope): boolean {
  if (review.reviewCycleId !== scope.selectedCycleId) return false;
  if (scope.isSuperAdmin) return true;
  if (scope.teamMemberEmails.has(review.employeeId.trim().toLowerCase())) return true;
  return sameIdentity(review.reviewerId, scope.managerId);
}

export interface TeamReviewProgressStats {
  employees: number;
  waitingEmployee: number;
  selfReviewSubmitted: number;
  managerCompleted: number;
}

export function computeTeamReviewProgress(
  memberEmails: string[],
  reviews: Review[]
): TeamReviewProgressStats {
  const stats: TeamReviewProgressStats = {
    employees: memberEmails.length,
    waitingEmployee: 0,
    selfReviewSubmitted: 0,
    managerCompleted: 0,
  };

  for (const email of memberEmails) {
    /*
     * The member's FURTHEST-PROGRESSED review, not the first one listed.
     *
     * With `.find`, an employee holding a duplicate pair was classified by whichever record the
     * API happened to return first — so someone who had submitted could be counted under
     * "Waiting Employee" while the queue simultaneously showed their submitted record. Counting
     * by member email already guarantees one tally per person; ranking makes that tally the
     * right one.
     */
    const memberReview = reviews
      .filter(r => sameIdentity(r.employeeId, email))
      .sort((a, b) => reviewProgressRank(b) - reviewProgressRank(a))[0];
    switch (getSelfReviewProgress(memberReview)) {
      case SelfReviewProgress.MANAGER_SUBMITTED:
        stats.managerCompleted += 1;
        break;
      case SelfReviewProgress.SELF_SUBMITTED:
        stats.selfReviewSubmitted += 1;
        break;
      default:
        // Includes members with no review record at all — nobody has acted, so the work
        // still sits with the employee.
        stats.waitingEmployee += 1;
    }
  }

  return stats;
}

export interface ManagerReviewProgressStats {
  total: number;
  completed: number;
  inProgress: number;
  pending: number;
  submitted: number;
}

export function computeManagerReviewProgress(reviews: Review[]): ManagerReviewProgressStats {
  const stats: ManagerReviewProgressStats = {
    total: reviews.length,
    completed: 0,
    inProgress: 0,
    pending: 0,
    submitted: 0,
  };
  for (const review of reviews) {
    const status = inferReviewWorkflowStatus(review);
    switch (status) {
      case ReviewWorkflowStatus.COMPLETED:
        stats.completed += 1;
        break;
      case ReviewWorkflowStatus.IN_PROGRESS:
        stats.inProgress += 1;
        break;
      case ReviewWorkflowStatus.SUBMITTED:
        stats.submitted += 1;
        break;
      default:
        stats.pending += 1;
    }
  }
  return stats;
}

export interface HRReviewMonitoringStats {
  total: number;
  draft: number;
  inProgress: number;
  submitted: number;
  completed: number;
  overdue: number;
  acknowledged: number;
  pendingAcknowledgement: number;
  completionPercentage: number;
  acknowledgementRate: number;
}

export function computeHRReviewStats(
  reviews: Review[],
  cycle?: ReviewCycle | null
): HRReviewMonitoringStats {
  const stats: HRReviewMonitoringStats = {
    total: reviews.length,
    draft: 0,
    inProgress: 0,
    submitted: 0,
    completed: 0,
    overdue: 0,
    acknowledged: 0,
    pendingAcknowledgement: 0,
    completionPercentage: 0,
    acknowledgementRate: 0,
  };
  const now = Date.now();
  const dueDate = cycle?.endYearDeadline || cycle?.submissionDeadline;

  for (const review of reviews) {
    const status = inferReviewWorkflowStatus(review);
    switch (status) {
      case ReviewWorkflowStatus.DRAFT:
        stats.draft += 1;
        break;
      case ReviewWorkflowStatus.IN_PROGRESS:
        stats.inProgress += 1;
        break;
      case ReviewWorkflowStatus.SUBMITTED:
        stats.submitted += 1;
        break;
      case ReviewWorkflowStatus.COMPLETED:
        stats.completed += 1;
        break;
    }
    if (
      dueDate &&
      new Date(dueDate).getTime() < now &&
      status !== ReviewWorkflowStatus.SUBMITTED &&
      status !== ReviewWorkflowStatus.COMPLETED
    ) {
      stats.overdue += 1;
    }
    if (review.isAcknowledged) {
      stats.acknowledged += 1;
    } else if (
      status === ReviewWorkflowStatus.SUBMITTED ||
      status === ReviewWorkflowStatus.COMPLETED
    ) {
      stats.pendingAcknowledgement += 1;
    }
  }

  const finished = stats.submitted + stats.completed;
  stats.completionPercentage = stats.total > 0 ? Math.round((finished / stats.total) * 100) : 0;
  const publishable = finished;
  stats.acknowledgementRate =
    publishable > 0 ? Math.round((stats.acknowledged / publishable) * 100) : 0;
  return stats;
}

export function getDaysOverdue(dueDate: string): number {
  const due = new Date(dueDate).getTime();
  const diff = Date.now() - due;
  return diff > 0 ? Math.ceil(diff / (1000 * 60 * 60 * 24)) : 0;
}

/** "3 days ago" / "Today" / "in 2 days" — used for submission timestamps across the review UI. */
export function formatRelativeTime(dateString?: string): string {
  if (!dateString) return '';
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return '';
  const diffDays = Math.round((then - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === -1) return 'yesterday';
  if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  if (diffDays === 1) return 'tomorrow';
  return `in ${diffDays} days`;
}
